import { Tween } from "../../../vendor_core/core/src/Tween";
import SpinModel from "../../../vendor_core/core/src/model/SpinModel";
import { IOverlayData, ISymbolPosition, OverlayType } from "../../../vendor_core/core/src/server/data/ISpinData";
import { PlayAnimatedExpand } from "../../../vendor_core/core/src/states/PlayAnimatedExpand";
import { BonusPositionTypes } from "../../../vendor_core/core/src/server/data/BonusPositionTypes";
import { Rectangle, Point, Graphics } from "pixi.js";
import { Timeline } from "../../../vendor_core/core/src/Timeline";
import { DefaultLayers } from "../../../vendor_core/core/src/utils/Layers";
import Layout from "../../../vendor_core/core/src/utils/Layout";
import ViewManager from "../../../vendor_core/core/src/utils/ViewManager";
import BitmapTextField from "../../../vendor_core/core/src/views/components/BitmapTextField";
import SymbolAnimation from "../../../vendor_core/core/src/views/SymbolAnimation";
import Utils from "../../../vendor_core/core/src/utils/Utils";
import VOEReelGroup from "../view/VOEReelGroup";
import PromiseHelpers from "../../../vendor_core/core/src/utils/PromiseHelpers";
import SoundController from "../../../vendor_core/core/src/utils/SoundController";
import GameSoundVars from "../enums/GameSoundVars";
import MYSymbolAnimation from "../view/MYSymbolAnimation";

export class SaSPlayAnimatedExpandState extends PlayAnimatedExpand {
    private reelGroup: VOEReelGroup;
    private setIdDelay: number = 0.4;
    private idToSet: number = 15;
    private lastMoveData: {pos: Point, el: BitmapTextField} = {
        el: null,
        pos: null
    }

    enter() {
        this.reelGroup = this.getReelGroup();
        this.reelGroup.darkedSymbols([14])
        // Wait some time for darken anim finish
        super.enter()
        // Tween.delayedCall(0.5, () => )
    }

    /**
     * Add specific suffix to the animation if needed
     * @param reelId
     * @param rowId
     */
    protected getExpandAnimSuffix(reelId: number, rowId: number) {
        let result = "expanding_chain";
        if (this.reelGroup.getSymbolAt(reelId, rowId).id === 14) {
            result = "black_hole_loop"
        }
        return result;
    }

    /**
     * Retrieve loop animation name
     * @param reelId
     * @param rowId
     */
    protected getLoopAnimName(reelId: number, rowId: number) {
        // No win loop animation for this one
        return undefined;
    }

    protected getReelGroup() {
        // Get the reel where we are expanding
        const scene = ViewManager.getLayer(
            DefaultLayers.Reels
        ).getCurrentScene();
        return scene.getSubView("ReelGroup") as VOEReelGroup;
    }

    protected getExpandPromise(overlayType: OverlayType) {
        const promises = new Array<Promise<void>>();
        if (overlayType != OverlayType.Expanding) {
            return PromiseHelpers.combineParallel(promises);
        }

        const mergedCells = new Array<ISymbolPosition>();
        let currentMergeIdx = 0;
        SpinModel.bonuses.forEach(bonus => {
            if (bonus.type == BonusPositionTypes.SymbolMerge) {
                bonus.symbolPositions.forEach((pos, idx) => {
                    if (!mergedCells.some(d => d.reel === pos.reel && d.row === pos.row)) {
                        const reel = pos.reel;
                        const row = pos.row;
                        // const id = bonus.symbolId;
                        // debugger
                        const promise = this.createMergePromise(reel, row, currentMergeIdx, mergedCells.length);
                        mergedCells.push({ reel, row });
                        promises.push(promise);
                    }
                })

                currentMergeIdx++;
            }
        });

        promises.push(this.createExpandPromise(overlayType, mergedCells));

        this.shiftMultipliers();
        return PromiseHelpers.combineParallel(promises);
    }

    private createMergePromise(reel: number, row: number, mergeIndex: number, positionIndex: number) {
        return new Promise<void>((resolve) => {
            const symbol = this.reelGroup.getSymbolAt(reel, row);
            const delay = this.reelGroup.mulipliersConf.delayBetweenBlackHoles * mergeIndex + this.reelGroup.mulipliersConf.delayBetweenFlames * positionIndex;
            Tween.delayedCall(delay, () => {
                // Tween.delayedCall(this.setIdDelay, () => {
                    symbol.setId(this.idToSet)
                // });

                symbol.playAnimation(this.getExpandAnimSuffix(reel, row))
                const anim = this.reelGroup.winSymbolsView.getAnimAtReelCell(reel,  row);

                anim.showAnim(this.idToSet, this.getExpandAnimSuffix(reel, row))
                .then(() => {
                    Tween.delayedCall(2, () => {
                        resolve();
                    });
                });
            })
        });
    }

    protected createExpandPromise(overlayType: OverlayType, mergedCells: ISymbolPosition[]) {
        const promise = new Promise<void>((resolve) => {
            const overlay = SpinModel.getOverlay(overlayType);

            if (overlay) {
                this.playBlackHoleExpandSounds(overlay);
                let expandedCellsCount = 0;
                let counter = overlay.initPositions?.length || 0;
                overlay.symbolPosition?.forEach((position, reel) => {
                    position.forEach((id, row) => {
                        if (mergedCells.find(x => x.reel == reel && x.row == row)) {
                            // This cell gonna merge.
                            expandedCellsCount++;
                            resolve();
                            return;
                        }

                        if (id != null) {
                            const delay = this.reelGroup.mulipliersConf.delayBetweenFlames * expandedCellsCount;
                            Tween.delayedCall(delay, () => {
                                const symbol = this.reelGroup.getSymbolAt(reel, row);
                                // Tween.delayedCall(this.setIdDelay, () => {
                                    symbol.setId(this.idToSet)
                                // });

                                symbol.playAnimation(this.getExpandAnimSuffix(reel, row))
                                const anim = this.reelGroup.winSymbolsView.getAnimAtReelCell(reel,  row);

                                anim.showAnim(this.idToSet, this.getExpandAnimSuffix(reel, row))
                                .then(() => {
                                    counter--;
                                    if (counter === 0) {
                                        this.reelGroup.sortSymbols();
                                        // Then exit
                                        Tween.delayedCall(1, () => {
                                            resolve();
                                        })
                                    }
                                });
                            });

                            expandedCellsCount++;
                        }
                    });
                });
            } else {
                // If there is no such overlay - resolve directly
                resolve();
            }
        });

        return promise;
    }

    playBlackHoleExpandSounds(overlay: IOverlayData) {
        overlay.initPositions.forEach((pos, index) => {
            Tween.delayedCall(this.reelGroup.mulipliersConf.delayBetweenBlackHoles * index, () => {
                SoundController.playSound(GameSoundVars.BLACK_SPELL_EXPAND);
            }, [], this);
        });
    }

    private shiftMultipliers(): void {
        const blackHoleMultipliers: IBlackHoleMultiplier[] = [];

        const timeline = new Timeline();
        let currentMergeIdx = 0;
        let currentExpandIdx = 0;
        SpinModel.bonuses?.forEach(bonus => {
            if (bonus.type == BonusPositionTypes.SymbolMerge) {
                const composite = this.getCompositeBound(bonus.symbolPositions);
                blackHoleMultipliers.push({
                    composite: composite,
                    anim: this.reelGroup.winSymbolsView.getAnimAtReelCell(bonus.symbolPositions[0].reel, bonus.symbolPositions[0].row),
                    multi: bonus.multiplier
                });

                if (this.reelGroup.mulipliersConf.debugDraw) {
                    this.debugDrawBHRects(composite, blackHoleMultipliers.length);
                }

                currentMergeIdx++;
            } else if (bonus.type == BonusPositionTypes.ExpandingSymbol) {
                // The first element is always the origin
                // The second is the expand direction (could be negative and more than 1).
                const origin = bonus.symbolPositions[0];
                const direction = bonus.symbolPositions[1];

                const composite = this.getCompositeBound(
                    [origin, { reel: origin.reel + direction.reel, row: origin.row + direction.row}]
                );

                const anim = this.reelGroup.winSymbolsView.getAnimAtReelCell(origin.reel, origin.row);
                if (currentMergeIdx) {
                    // Moving the multipliers in sequence
                    this.moveToCenter([anim], composite, timeline, this.reelGroup.mulipliersConf.delayBetweenBlackHoles * currentExpandIdx);
                } else {
                    // Moving the multipliers together
                    this.moveToCenter([anim], composite, timeline, 0);
                }

                currentExpandIdx++;
            }
        });

        if (currentExpandIdx > 0) {
            const delay = timeline.totalDuration + this.reelGroup.mulipliersConf.bh_delayBetweenShifts;
            Tween.delayedCall(delay + this.reelGroup.mulipliersConf.bh_shiftTime, () => { this.mergeBlackHoleMultipliers(blackHoleMultipliers); }, [], this);
        }
    }

    private moveToCenter(anims: SymbolAnimation[], rect: Rectangle, timeline: Timeline, position: number, opacity: number = 1) {
        const center = new Point(rect.x + rect.width / 2, rect.y + rect.height / 2);

        anims.forEach((anim, i, arr) => {
            const text = anim.getTextOverlay() as BitmapTextField;
            if (text) {
                const translated = anim.toLocal(this.reelGroup.winSymbolsView.toGlobal(center));
                if (i === (arr.length - 1)) {
                    this.lastMoveData.pos = translated;
                    this.lastMoveData.el = text;
                }
                timeline.to(text.position, this.reelGroup.mulipliersConf.bh_shiftTime, { x: translated.x, y: translated.y, ease: this.reelGroup.mulipliersConf.bh_shiftEase }, position);
                timeline.to(text, this.reelGroup.mulipliersConf.bh_shiftTime, { alpha: opacity }, position);
            }
        })
    }

    private getCompositeBound(positions: ISymbolPosition[]): Rectangle {
        const rects = new Array<Rectangle>();
        positions.forEach(symbolPosition => {
            const pos =this.reelGroup.winSymbolsView.getPositionAt(symbolPosition.reel, symbolPosition.row);
            const symbolWidth = Layout.scalePixels(this.reelGroup.symbolBounds.width);
            const symbolHeight = Layout.scalePixels(this.reelGroup.symbolBounds.height);
            const rect = new Rectangle(
                pos.x - symbolWidth / 2,
                pos.y - symbolHeight / 2,
                symbolWidth,
                symbolHeight
            );

            rects.push(rect);
        });

        const composite = Utils.createCompositeBound(rects, 1.0);
        return composite;
    }

    private debugDrawBHRects(rect: Rectangle, colorIndex: number) {
        const colors = [0xffffff, 0xff0000, 0x0000ff, 0x00ff00, 0xffff00];
        const gr = new Graphics();
        gr.beginFill(colors[colorIndex % colors.length], 0.1);
        gr.lineStyle(Layout.scalePixels(7), colors[colorIndex % colors.length], 0.3);
        gr.drawRect(rect.x, rect.y, rect.width, rect.height);
        gr.name = this.reelGroup.mulipliersConf.debugName;
        this.reelGroup.winSymbolsView.addChild(gr);
    }

    private playMergedMultipliersExplosionAnim = () => {
        if (this.lastMoveData.el && this.lastMoveData.pos) {
            // const translated = this.lastMoveData.el.toLocal(this.reelGroup.winSymbolsView.toGlobal(center));
            let sceneMultiplier = ViewManager.getLayer("MultiEffects").setScene("freespinsBN1");
            const mergeAnim = sceneMultiplier.spineAnimations.get("multi_merged");
            const elementToMove = mergeAnim.parent;

            // console.log("-------------------------------------");
            // console.log(this.lastMoveData.el.children[0].getGlobalPosition());
            // console.log(this.lastMoveData.el.getGlobalPosition());
            // console.log(this.lastMoveData.el.parent.parent.toGlobal(this.lastMoveData.pos));
            // console.log("-------------------------------------");
            const pos = elementToMove.parent.toLocal(this.lastMoveData.el.getGlobalPosition())
            // const pos = elementToMove.parent.toLocal(this.lastMoveData.el.children[0].getGlobalPosition())
            // const pos1 = elementToMove.parent.toLocal(this.lastMoveData.el.parent.parent.toGlobal(this.lastMoveData.pos))

            // console.log("=========================");
            // console.log(pos);
            // console.log(pos1);
            // console.log("=========================");

            const newX = pos.x - elementToMove.width / 2;
            const newY = pos.y - elementToMove.height / 2;
            // console.log(newX);
            // console.log(newY);


            // console.log(center);
            // console.log(this.reelGroup.winSymbolsView.toGlobal(center));
            // console.log(elementToMove.toGlobal(center));
            // const translated = elementToMove.toLocal(elementToMove.parent.toGlobal(center));
            // console.log(translated);

            // const parent = this.lastMoveData.el.children[0] as BitmapText;
            // parent.addChild(mergeAnim.parent);
            // // const newX = this.lastMoveData.pos.x - mergeAnim.parent.width / 2;
            // // const newY = this.lastMoveData.pos.y - mergeAnim.parent.height / 2;
            // const newX = translated.x;
            // const newY = translated.y;
            elementToMove.position.set(newX, newY);
            mergeAnim.visible = true;
            mergeAnim.play(null, false, 0.9);

            // // mergeAnim.playAndHide();
            // // mergeAnim.playAsync().then(() => text.visible = true)
        }
    }

    private mergeBlackHoleMultipliers(blackHoles: IBlackHoleMultiplier[]) {
        if (blackHoles.length == 0) {
            return;
        }

        const commonRect = Utils.createCompositeBound(blackHoles.map(x => x.composite), 1.0);

        const anims = blackHoles.map(x => x.anim);
        const timeline = new Timeline();

        this.moveToCenter(anims, commonRect, timeline, 0, 0.1);
        const position = timeline.totalDuration;
        blackHoles.forEach((blackHole) => {
            const text = (blackHole.anim as MYSymbolAnimation).getTextOverlay(0) as BitmapTextField;
            const text2 = (blackHole.anim as MYSymbolAnimation).getTextOverlay(1) as BitmapTextField;
            // text2 is second bmp font used after merge all texts(1)
            if (text && text2) {
                text.autoApplyBound = false;

                timeline.set(text, {
                    alpha: 0,
                    text: `x${blackHole.multi}`
                }, position);

                timeline.call(() => {
                    this.playMergedMultipliersExplosionAnim();

                    text2.text = `x${blackHole.multi}`;
                    text2.alpha = 1;
                    text2.x = text.x;
                    text2.y = text.y;
                }, null, undefined, position)

                const scaleX = text.scale.x;
                const scaleY = text.scale.y;

                // Applying bumping animation
                timeline.to(
                    text2.scale,
                    this.reelGroup.mulipliersConf.scaleTime,
                    { x: scaleX * this.reelGroup.mulipliersConf.scaleEndValue * 1.5, y: scaleY * this.reelGroup.mulipliersConf.scaleEndValue * 1.5 },
                    position + this.reelGroup.mulipliersConf.scaleTime,
                );

                // Increasing the multiplier scale to its end value
                timeline.to(
                    text2.scale,
                    this.reelGroup.mulipliersConf.scaleTime,
                    { x: scaleX * this.reelGroup.mulipliersConf.scaleEndValue, y: scaleY * this.reelGroup.mulipliersConf.scaleEndValue },
                    position + this.reelGroup.mulipliersConf.scaleTime * 2
                );
            }
        });
    }
}

interface IBlackHoleMultiplier {
    composite: Rectangle,
    anim: SymbolAnimation,
    multi: number
}
